/**
 * DiskGelo — premium SVG cursor.
 *
 * Architecture:
 *   - Two SVG elements inside #fx-overlay: #cursor-ring (hollow circle)
 *     and #cursor-dot (small filled dot)
 *   - Dot snaps to exact mouse position (no lerp) — crisp
 *   - Ring lerps with spring-ish ease toward mouse — elegant trail
 *   - Hover states:
 *       - over a button → ring expands to ~48px, stroke thickens, red accent
 *       - over a link   → ring expands to ~40px, cyan accent
 *       - over an input → ring shrinks, hidden
 *   - Uses gsap.ticker so we share the single RAF loop with the background
 */
import { gsap } from 'gsap'
import type { Env } from '../env'

export interface CursorHandle {
  dispose(): void
}

export function mountCursor(env: Env): CursorHandle | null {
  // Skip on touch / reduced-motion
  if (env.isTouch) return null

  const ringEl = document.getElementById('cursor-ring') as unknown as SVGCircleElement | null
  const dotEl = document.getElementById('cursor-dot') as unknown as SVGCircleElement | null
  if (!ringEl || !dotEl) return null

  // Non-null locals preserve narrowing across closures
  const ring: SVGCircleElement = ringEl
  const dot: SVGCircleElement = dotEl

  document.body.classList.add('cursor-active')

  let mouseX = window.innerWidth / 2
  let mouseY = window.innerHeight / 2
  let ringX = mouseX
  let ringY = mouseY

  type HoverState = 'idle' | 'button' | 'link' | 'text'
  let hover: HoverState = 'idle'

  function applyHoverStyles() {
    switch (hover) {
      case 'button':
        gsap.to(ring, {
          attr: { r: 32 },
          duration: 0.45,
          ease: 'power3.out',
          overwrite: true,
        })
        ring.setAttribute('stroke', '#e0231c')
        ring.setAttribute('stroke-width', '2')
        ring.setAttribute('opacity', '0.9')
        dot.setAttribute('opacity', '0')
        break
      case 'link':
        gsap.to(ring, {
          attr: { r: 26 },
          duration: 0.45,
          ease: 'power3.out',
          overwrite: true,
        })
        ring.setAttribute('stroke', '#00b8d9')
        ring.setAttribute('stroke-width', '1.6')
        ring.setAttribute('opacity', '0.9')
        dot.setAttribute('opacity', '1')
        break
      case 'text':
        gsap.to(ring, {
          attr: { r: 2 },
          duration: 0.3,
          ease: 'power3.out',
          overwrite: true,
        })
        ring.setAttribute('opacity', '0')
        dot.setAttribute('opacity', '0')
        break
      default:
        gsap.to(ring, {
          attr: { r: 20 },
          duration: 0.45,
          ease: 'power3.out',
          overwrite: true,
        })
        ring.setAttribute('stroke', '#0a1828')
        ring.setAttribute('stroke-width', '1.5')
        ring.setAttribute('opacity', '0.85')
        dot.setAttribute('opacity', '1')
    }
  }

  function setHover(next: HoverState) {
    if (next === hover) return
    hover = next
    applyHoverStyles()
  }

  // Initial render
  applyHoverStyles()

  // Mouse tracking
  function onMove(e: MouseEvent) {
    mouseX = e.clientX
    mouseY = e.clientY
  }
  window.addEventListener('mousemove', onMove, { passive: true })

  // Hover state via delegated mouseover
  function onOver(e: MouseEvent) {
    const target = e.target as Element | null
    if (!target) return
    if (target.closest('input[type="text"], input[type="email"], input[type="tel"], textarea')) {
      setHover('text')
    } else if (target.closest('button, .ice-button, [role="button"]')) {
      setHover('button')
    } else if (target.closest('a')) {
      setHover('link')
    } else {
      setHover('idle')
    }
  }
  document.addEventListener('mouseover', onOver, { passive: true })

  // Click feedback: flash-scale the ring
  function onClick() {
    gsap.fromTo(
      ring,
      { attr: { r: 8 } },
      {
        attr: { r: hover === 'button' ? 32 : 20 },
        duration: 0.5,
        ease: 'elastic.out(1, 0.5)',
      },
    )
  }
  window.addEventListener('mousedown', onClick)

  // Leave / enter visibility
  function onLeave() {
    gsap.to([ring, dot], { opacity: 0, duration: 0.2 })
  }
  function onEnter() {
    applyHoverStyles()
  }
  window.addEventListener('mouseleave', onLeave)
  window.addEventListener('mouseenter', onEnter)

  // RAF tick via gsap.ticker (shared loop)
  function tick(_time: number, _dt: number) {
    // Dot at exact mouse position
    dot.setAttribute('cx', String(mouseX))
    dot.setAttribute('cy', String(mouseY))

    // Ring lerps smoothly behind
    const lerp = 0.22
    ringX += (mouseX - ringX) * lerp
    ringY += (mouseY - ringY) * lerp
    ring.setAttribute('cx', ringX.toFixed(2))
    ring.setAttribute('cy', ringY.toFixed(2))
    void _time
    void _dt
  }
  gsap.ticker.add(tick)

  return {
    dispose() {
      gsap.ticker.remove(tick)
      window.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseover', onOver)
      window.removeEventListener('mousedown', onClick)
      window.removeEventListener('mouseleave', onLeave)
      window.removeEventListener('mouseenter', onEnter)
      document.body.classList.remove('cursor-active')
    },
  }
}
